import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle } from '../components/icons';
import { supabase } from '../lib/supabase';

const REPLAY_URL = 'https://app.humanpotentialos.com/foundations-replay-day1/thankyou';

export function EventRegistrationPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const { error: insertError } = await supabase
        .from('event_registrations')
        .insert({
          full_name: form.full_name.trim(),
          email: form.email.trim().toLowerCase(),
          event_slug: 'foundations-day1-replay',
        });

      if (insertError) throw insertError;

      supabase.functions.invoke('send-email', {
        body: {
          type: 'replay-access',
          recipientEmail: form.email.trim().toLowerCase(),
          recipientName: form.full_name.trim(),
          replayUrl: REPLAY_URL,
        },
      }).catch(() => {});

      navigate('/foundations-replay-day1/thankyou');
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-teal-500/20 border border-teal-400/30 text-teal-300 text-xs font-semibold px-3 py-1.5 rounded-full mb-5 uppercase tracking-wide">
            Free Replay
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight mb-4">
            Watch the Day 1 Replay
          </h1>
          <h2 className="text-teal-300 text-lg font-medium mb-4">
            NLP OS™ Foundations Certification
          </h2>
          <p className="text-slate-300 text-sm leading-relaxed max-w-md mx-auto">
            Couldn't make it live, or want to rewatch? Enter your details below and we'll send you the link — yours to keep.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-7 shadow-2xl"
        >
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2.5 rounded-xl mb-5 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                First Name
              </label>
              <input
                type="text"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                required
                placeholder="Your first name"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-shadow"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                placeholder="you@example.com"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-shadow"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3.5 bg-teal-600 hover:bg-teal-700 active:bg-teal-800 text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-50 shadow-sm"
            >
              {isSubmitting ? 'Loading...' : 'Watch the Replay →'}
            </button>
          </form>

          <p className="text-xs text-gray-400 text-center mt-4">
            We'll also email you the link so you can watch it anytime. No spam, unsubscribe anytime.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="flex items-center justify-center gap-3 mt-8"
        >
          <img
            src="/Patrick_Profile_Under_2MB_q95.jpg"
            alt="Patrick Dahdal"
            className="w-10 h-10 rounded-full object-cover border-2 border-teal-400/50"
          />
          <div>
            <p className="text-white text-sm font-semibold">Patrick Dahdal</p>
            <p className="text-slate-400 text-xs">Founder, NLP OS™</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
